import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function SeoLanding() {
  const { slug } = useParams();
  const { locale, t } = useI18n();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null);
    setErr(false);
    api.get(`/pages/${locale}/${slug}`).then(({ data }) => setP(data)).catch(() => setErr(true));
  }, [locale, slug]);

  if (err)
    return (
      <div className="px-6 pt-48 text-center" data-testid="seo-page-404">
        <h1 className="font-display text-4xl font-semibold">404</h1>
        <p className="mt-4 text-[#9194a6]">
          {locale === "fr" ? "Cette page n'existe pas." : "This page does not exist."}
        </p>
        <Link to={`/${locale}`} className="btn-ghost mt-8 inline-block rounded-full px-6 py-3 text-sm">
          {locale === "fr" ? "Retour à l'accueil" : "Back home"}
        </Link>
      </div>
    );
  if (!p) return <div className="px-6 pt-48 text-center text-[#9194a6]">…</div>;

  const s = p.sections;

  return (
    <div className="pt-16" data-testid="seo-landing">
      <Seo
        title={p.title}
        description={p.meta_description}
        locale={locale}
        path={`/${locale}/${p.slug}`}
        altPath={`/${locale === "en" ? "fr" : "en"}/${p.alt_slug}`}
        image={p.hero_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/${locale}` },
                { "@type": "ListItem", position: 2, name: p.h1 },
              ],
            },
            {
              "@type": "FAQPage",
              mainEntity: (p.faq || []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />

      <section className="relative grain overflow-hidden px-6 pb-20 pt-20 lg:px-10 lg:pt-28">
        <img src={p.hero_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#050508]" />
        <div className="relative mx-auto max-w-[1400px]">
          <nav className="mono flex gap-2 text-[11px] uppercase tracking-[0.16em] text-[#8288a0]">
            <Link to={`/${locale}`} className="hover:text-white">
              {locale === "fr" ? "Accueil" : "Home"}
            </Link>
            <span>/</span>
            <span className="text-[#9194a6]">{p.group}</span>
          </nav>
          <h1 className="mt-7 max-w-[900px] font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
            {p.h1}
          </h1>
          <p className="mt-7 max-w-[680px] text-base leading-relaxed text-[#c0c3d0] md:text-lg">
            {p.meta_description}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to={`/${locale}/register`}
              data-testid="seo-cta-primary"
              className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
            >
              {s.cta} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={`/${locale}/pricing`} className="btn-ghost rounded-full px-7 py-3.5 text-sm font-semibold">
              {t("nav.pricing")}
            </Link>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[880px] space-y-6">
          <p className="text-base leading-[1.8] text-[#c0c3d0] md:text-lg">{s.intro}</p>
          {s.intro2 && <p className="text-base leading-[1.8] text-[#9194a6]">{s.intro2}</p>}
          {s.intro3 && <p className="text-base leading-[1.8] text-[#9194a6]">{s.intro3}</p>}
        </div>
      </section>

      <section className="border-y border-white/[0.06] bg-[#08080d] px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">{locale === "fr" ? "Ce que vous obtenez" : "What you get"}</p>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-[#1a1b26] bg-[#1a1b26] sm:grid-cols-2 lg:grid-cols-3">
            {s.features.map((f) => (
              <div key={f.t} className="bg-[#0b0b11] p-7 transition-colors duration-300 hover:bg-[#101118]">
                <Check className="h-4 w-4 text-[#00ff9d]" />
                <h3 className="mt-5 font-display text-base font-medium">{f.t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-[#9194a6]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">{locale === "fr" ? "Comment ça marche" : "How it works"}</p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {s.steps.map((st) => (
              <div key={st.t} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-6">
                <p className="mono text-[11px] text-[#ff4b2b]">{st.t}</p>
                <p className="mt-3 text-[13.5px] leading-relaxed text-[#c0c3d0]">{st.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {[
        ["engines", locale === "fr" ? "Les moteurs vidéo disponibles" : "The available video engines", 3],
        ["use_cases", locale === "fr" ? "Cas d'usage et budgets réels" : "Real use cases and budgets", 2],
        ["pricing", locale === "fr" ? "Combien ça coûte en Movies Credits" : "What it costs in Movies Credits", 3],
        ["mistakes", locale === "fr" ? "Les cinq erreurs à éviter" : "Five mistakes to avoid", 2],
        ["glossary", locale === "fr" ? "Glossaire" : "Glossary", 3],
      ].map(([key, heading, cols], idx) =>
        (s[key] || []).length ? (
          <section
            key={key}
            data-testid={`seo-section-${key}`}
            className={`px-6 py-20 lg:px-10 ${idx % 2 === 0 ? "border-y border-white/[0.06] bg-[#08080d]" : ""}`}
          >
            <div className="mx-auto max-w-[1400px]">
              <h2 className="font-display text-3xl font-semibold sm:text-4xl">{heading}</h2>
              <div
                className={`mt-10 grid gap-5 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : ""}`}
              >
                {s[key].map((it) => (
                  <div key={it.t} className="rounded-xl border border-[#212230] bg-[#0b0b11] p-6">
                    <h3 className="font-display text-base font-medium">{it.t}</h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-[#9194a6]">{it.d}</p>
                    {it.price && (
                      <p className="mono mt-3 text-[11px] text-[#ff4b2b]">{it.price}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null
      )}

      <section className="border-t border-white/[0.06] bg-[#08080d] px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-[1000px]">
          <h2 className="font-display text-3xl font-semibold sm:text-4xl">{t("faq.title")}</h2>
          <div className="mt-10 divide-y divide-white/[0.07]">
            {(p.faq || []).map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                  <span className="font-display text-base font-medium">{f.q}</span>
                  <span className="mono mt-1 shrink-0 text-[#ff4b2b] transition-transform duration-300 group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-[#9194a6]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {p.related?.length > 0 && (
        <section className="px-6 py-20 lg:px-10">
          <div className="mx-auto max-w-[1400px]">
            <p className="overline">{locale === "fr" ? "Pages liées" : "Related pages"}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {p.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/${locale}/${r.slug}`}
                  className="rounded-full border border-[#2a2c3c] px-4 py-2 text-[12.5px] text-[#9194a6] transition-colors duration-200 hover:border-[#6a7090] hover:text-white"
                >
                  {r.h1}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
